import React, { useState, useEffect, useRef } from 'react';
import { Search, ArrowUp, ArrowDown, Calendar, Filter, MoreHorizontal, Edit, Trash2,
  Scissors, AlignJustify, CalendarDays, CalendarRange,
  HelpCircle, X, Check, Download } from 'lucide-react';
import { useTimezone } from '../../../../../hooks/useTimezone';
import { formatInTimezone, formatTimeOnly, formatDateOnly } from '../../../../../utils/timezoneUtils';
import SplitEntryModal from './SplitEntryModal';
import { useSocket } from '../../../../../hooks/useSocket';
import { useAuth } from '../../../../../context/AuthContext';
import DownloadProgressToast from '../../../../../components/DownloadProgressToast';

// Helper function to get today's date range in user's timezone
const getTodayDateRange = (userTimezone = 'America/Chicago') => {
  const now = new Date();
  
  // Get start of today in user's timezone
  const startOfDay = new Date(now.toLocaleString('en-US', { timeZone: userTimezone }));
  startOfDay.setHours(0, 0, 0, 0);
  
  // Get end of today in user's timezone
  const endOfDay = new Date(now.toLocaleString('en-US', { timeZone: userTimezone }));
  endOfDay.setHours(23, 59, 59, 999);
  
  return {
    start: startOfDay.toISOString(),
    end: endOfDay.toISOString(),
    displayDate: new Date(now.toLocaleString('en-US', { timeZone: userTimezone }))
  };
};

// Helper function to get past 7 days date range in user's timezone
const getPast7DaysRange = (userTimezone = 'America/Chicago') => {
  const now = new Date();
  const today = new Date(now.toLocaleString('en-US', { timeZone: userTimezone }));
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6); // 6 days ago + today = 7 days
  
  // Start of 7 days ago in user's timezone
  const startOfRange = new Date(sevenDaysAgo);
  startOfRange.setHours(0, 0, 0, 0);
  
  // End of today in user's timezone
  const endOfRange = new Date(today);
  endOfRange.setHours(23, 59, 59, 999);
  
  return {
    start: startOfRange.toISOString(),
    end: endOfRange.toISOString(),
    displayDate: today
  };
};


const ClockInEntriesTab = () => {
  const userTimezone = useTimezone();
  const { currentUser } = useAuth();
  const { socket } = useSocket(currentUser?.id);
  // State for entries data
  const [entries, setEntries] = useState([]);
  // State for loading
  const [loading, setLoading] = useState(true);
  // State for search term
  const [searchTerm, setSearchTerm] = useState('');
  // State for current date view
  const [currentDateView, setCurrentDateView] = useState(() => getTodayDateRange(userTimezone));
  const [dateRangeType, setDateRangeType] = useState('today'); // 'today', 'past7days', 'custom'
  // State for active filters - initialize with today's date range using clock_in_time filtering
  const [filters, setFilters] = useState(() => {
    const todayRange = getTodayDateRange(userTimezone);
    return {
      clock_in_time_from: todayRange.start,
      clock_in_time_to: todayRange.end
    };
  });
  
  // Custom date range inputs
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  // State for active sorting
  const [sorting, setSorting] = useState({ column: 'clock_in_time', direction: 'desc' });
  // State for selected entry
  const [selectedEntry, setSelectedEntry] = useState(null);
  // State for edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  // State for split modal
  const [showSplitModal, setShowSplitModal] = useState(false);
  // State for visible columns
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    employee_id: false,
    full_name: true,
    activity: true,
    business_unit: true,
    clock_in_time: true,
    clock_out_time: true,
    total_time: true,
    group: false,
    project: false,
    shift: true,
    team: false,
    date: false,
    location: false,
    notes: false,
    reference1: false,
    reference2: false,
    reference3: false,
  });
  // State for column selector dropdown
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  // State for filter options
  const [filterOptions, setFilterOptions] = useState({
    activity: [],
    business_unit: [],
    full_name: []
  });
  // State for search help modal
  const [showSearchHelp, setShowSearchHelp] = useState(false);
  // State for header filter dropdowns
  const [activeHeaderDropdown, setActiveHeaderDropdown] = useState(null);
  const [pendingHeaderFilters, setPendingHeaderFilters] = useState({});
  // State for global search
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  // State for export modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  // State for download progress
  const [downloadProgress, setDownloadProgress] = useState(null);
  const abortControllerRef = useRef(null);
  // Ref for table container to control scroll
  const tableContainerRef = useRef(null);

  // Define column types for appropriate filter/sort options
  const columnTypes = {
    id: 'integer',
    employee_id: 'integer',
    full_name: 'select', // Multi-select dropdown with search
    activity: 'select', // Multi-select dropdown
    business_unit: 'select', // Multi-select dropdown
    clock_in_time: 'datetime',
    clock_out_time: 'datetime',
    total_time: 'time',
    group: 'text',
    project: 'text',
    shift: 'text',
    team: 'text',
    date: 'date',
    location: 'text',
    notes: 'text',
    reference1: 'text',
    reference2: 'text',
    reference3: 'text',
  };

  // Define column widths for consistent sizing
  const columnWidths = {
    id: '80px',
    employee_id: '120px',
    full_name: '160px',
    activity: '150px',
    business_unit: '150px',
    clock_in_time: '180px',
    clock_out_time: '180px',
    total_time: '120px',
    group: '120px',
    project: '120px',
    shift: '120px',
    team: '120px',
    date: '120px',
    location: '120px',
    notes: '200px',
    reference1: '120px',
    reference2: '120px',
    reference3: '120px',
    actions: '120px',
  };

  // All possible columns in the labor table
  const allColumns = [
    { id: 'id', label: 'ID', type: 'integer' },
    { id: 'employee_id', label: 'Employee ID', type: 'integer' },
    { id: 'full_name', label: 'Full Name', type: 'select' },
    { id: 'activity', label: 'Activity', type: 'select' },
    { id: 'business_unit', label: 'Business Unit', type: 'select' },
    { id: 'clock_in_time', label: 'Clock In', type: 'datetime' },
    { id: 'clock_out_time', label: 'Clock Out', type: 'datetime' },
    { id: 'total_time', label: 'Total Time', type: 'time' },
    { id: 'group', label: 'Group', type: 'text' },
    { id: 'project', label: 'Project', type: 'text' },
    { id: 'shift', label: 'Shift', type: 'text' },
    { id: 'team', label: 'Team', type: 'text' },
    { id: 'date', label: 'Date', type: 'date' },
    { id: 'location', label: 'Location', type: 'text' },
    { id: 'notes', label: 'Notes', type: 'text' },
    { id: 'reference1', label: 'Reference 1', type: 'text' },
    { id: 'reference2', label: 'Reference 2', type: 'text' },
    { id: 'reference3', label: 'Reference 3', type: 'text' },
  ];

  // Custom date range handler
  const handleCustomDateRangeSearch = () => {
    if (!customStartDate || !customEndDate) {
      alert('Please select both start and end dates');
      return;
    }
    
    if (new Date(customStartDate) > new Date(customEndDate)) {
      alert('Start date cannot be after end date');
      return;
    }
    
    // Create date range from custom inputs in user's timezone
    const startOfRange = new Date(customStartDate + 'T00:00:00');
    const endOfRange = new Date(customEndDate + 'T23:59:59.999');
    
    const newDateRange = {
      start: startOfRange.toISOString(),
      end: endOfRange.toISOString(),
      displayDate: new Date(customEndDate) // Use end date for display
    };
    
    setCurrentDateView(newDateRange);
    setDateRangeType('custom');
    
    updateFilters({
      clock_in_time_from: newDateRange.start,
      clock_in_time_to: newDateRange.end
    });
  };

  // Handler for date range buttons
  const handleDateRangeChange = (rangeType) => {
    let newDateRange;
    
    switch (rangeType) {
      case 'today':
        newDateRange = getTodayDateRange(userTimezone);
        break;
      case 'past7days':
        newDateRange = getPast7DaysRange(userTimezone);
        break;
      default:
        return;
    }
    
    setCurrentDateView(newDateRange);
    setDateRangeType(rangeType);
    
    updateFilters({
      clock_in_time_from: newDateRange.start,
      clock_in_time_to: newDateRange.end
    });
  };

  // Helper function to update filters
  const updateFilters = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  };

  // Header filter management functions
  const handleHeaderFilterClick = (columnId, event) => {
    event.stopPropagation();
    setActiveHeaderDropdown(activeHeaderDropdown === columnId ? null : columnId);
  };

  const handleHeaderSortClick = (columnId, event) => {
    event.stopPropagation();
    const newDirection = sorting.column === columnId && sorting.direction === 'asc' ? 'desc' : 'asc';
    setSorting({ column: columnId, direction: newDirection });
  };

  const applyHeaderFilter = (columnId) => {
    const pendingFilter = pendingHeaderFilters[columnId];
    if (pendingFilter) {
      updateFilters({ [columnId]: pendingFilter });
    }
    setActiveHeaderDropdown(null);
    setPendingHeaderFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const cancelHeaderFilter = (columnId) => {
    setActiveHeaderDropdown(null);
    setPendingHeaderFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const clearHeaderFilter = (columnId) => {
    // Clear both pending and active filters for this column
    const newFilters = { ...filters };
    Object.keys(newFilters).forEach(key => {
      if (key === columnId || key.startsWith(columnId + '_')) {
        delete newFilters[key];
      }
    });
    setFilters(newFilters);
    
    setPendingHeaderFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
    
    setActiveHeaderDropdown(null);
  };

  const updatePendingHeaderFilter = (columnId, filterData) => {
    setPendingHeaderFilters(prev => ({
      ...prev,
      [columnId]: filterData
    }));
  };

  const hasActiveFilter = (columnId) => {
    // Check for filter parameters
    const hasFilterParam = Object.keys(filters).some(key => {
      return (key === columnId || key.startsWith(columnId + '_')) && filters[key];
    });
    
    return hasFilterParam;
  };

  const hasPendingFilter = (columnId) => {
    return pendingHeaderFilters[columnId] !== undefined;
  };

  const clearAllFilters = () => {
    // Keep only the date range filters that are always present
    const newFilters = {
      clock_in_time_from: filters.clock_in_time_from,
      clock_in_time_to: filters.clock_in_time_to
    };
    setFilters(newFilters);
    setGlobalSearchTerm('');
    setPendingHeaderFilters({});
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (globalSearchTerm) count++;
    
    // Count column filters (excluding the always-present date range)
    Object.keys(filters).forEach(key => {
      if (filters[key] && !key.startsWith('clock_in_time')) {
        count++;
      }
    });
    
    return count;
  };

  const getActiveFilterSummary = () => {
    const summary = [];
    
    if (globalSearchTerm) {
      summary.push(`Search: "${globalSearchTerm}"`);
    }
    
    Object.keys(filters).forEach(key => {
      if (filters[key] && !key.startsWith('clock_in_time')) {
        const column = allColumns.find(col => col.id === key || key.startsWith(col.id));
        if (column) {
          let filterDescription = filters[key];
          if (key.endsWith('_like')) {
            filterDescription = `contains "${filters[key]}"`;
          } else if (key.endsWith('_min')) {
            filterDescription = `min: ${filters[key]}`;
          } else if (key.endsWith('_max')) {
            filterDescription = `max: ${filters[key]}`;
          } else if (key.endsWith('_from')) {
            filterDescription = `from: ${new Date(filters[key]).toLocaleDateString()}`;
          } else if (key.endsWith('_to')) {
            filterDescription = `to: ${new Date(filters[key]).toLocaleDateString()}`;
          } else if (column.type === 'text') {
            filterDescription = `exact: "${filters[key]}"`;
          }
          summary.push(`${column.label}: ${filterDescription}`);
        }
      }
    });
    
    return summary;
  };

  // Fetch filter options for select dropdowns
  const fetchFilterOptions = async () => {
    try {
      // Fetch activities
      const activitiesResponse = await fetch('/api/activities', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (activitiesResponse.ok) {
        const activitiesData = await activitiesResponse.json();
        const activeActivities = activitiesData
          .filter(activity => activity.is_active)
          .map(activity => activity.name)
          .sort();
        
        // Fetch business units
        const businessUnitsResponse = await fetch('/api/business-units', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (businessUnitsResponse.ok) {
          const businessUnitsData = await businessUnitsResponse.json();
          const activeBusinessUnits = businessUnitsData
            .filter(unit => unit.is_active)
            .map(unit => unit.identifier)
            .sort();
          
          // Fetch employee names from users table
          const usersResponse = await fetch('/api/users', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          let employeeNames = [];
          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            employeeNames = usersData
              .map(user => user.full_name)
              .filter(name => name) // Filter out any null/undefined names
              .sort();
          }
          
          setFilterOptions({
            activity: activeActivities,
            business_unit: activeBusinessUnits,
            full_name: employeeNames
          });
        }
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch entries data
  useEffect(() => {
    const fetchEntries = async () => {
      setLoading(true);
      try {
        // Build query parameters based on search and filters
        let queryParams = new URLSearchParams();
        
        if (globalSearchTerm) {
          queryParams.append('search', globalSearchTerm);
        }
        
        // Add filters to query params
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value);
          }
        });
        
        
        // Add sorting to query params
        if (sorting.column) {
          queryParams.append('sort_by', sorting.column);
          queryParams.append('sort_direction', sorting.direction);
        }
        
        // Add a larger limit to ensure we get all entries for the day
        queryParams.append('limit', '10000');
        
        // Fetch data from API
        const response = await fetch(`/api/labor?${queryParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` 
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const data = await response.json();
        setEntries(data.entries || []);
      } catch (error) {
        console.error('Error fetching entries:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEntries();
  }, [globalSearchTerm, filters, sorting]);

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Listen for export job updates via WebSocket
  useEffect(() => {
    if (!socket) return;

    const handleExportUpdate = (data) => {
      // Only update if we're tracking this job
      if (downloadProgress && downloadProgress.jobId === data.jobId) {
        setDownloadProgress(prev => ({
          ...prev,
          status: data.status === 'completed' ? 'completed' : data.status === 'failed' ? 'error' : 'downloading',
          progress: data.progress || 0,
          processedRows: data.processedRows || 0,
          totalRows: data.totalRows || 0,
          filename: data.filename || prev.filename,
          error: data.error
        }));

        // If completed via WebSocket, trigger download
        if (data.status === 'completed') {
          // Fetch the file with authentication
          fetch(`/api/labor-export/${data.jobId}/download`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
          .then(response => response.blob())
          .then(blob => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', data.filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          })
          .catch(error => {
            console.error('Error downloading file:', error);
          });

          // Clear polling interval if it exists
          if (abortControllerRef.current && abortControllerRef.current.pollInterval) {
            clearInterval(abortControllerRef.current.pollInterval);
          }

          // Auto-close after 3 seconds
          setTimeout(() => {
            setDownloadProgress(null);
          }, 3000);
        }

        // If failed via WebSocket
        if (data.status === 'failed') {
          // Clear polling interval if it exists
          if (abortControllerRef.current && abortControllerRef.current.pollInterval) {
            clearInterval(abortControllerRef.current.pollInterval);
          }

          // Auto-close after 8 seconds
          setTimeout(() => {
            setDownloadProgress(null);
          }, 8000);
        }
      }
    };

    socket.on('export-job-update', handleExportUpdate);

    return () => {
      socket.off('export-job-update', handleExportUpdate);
    };
  }, [socket, downloadProgress]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showColumnSelector && !e.target.closest('.column-selector')) {
        setShowColumnSelector(false);
      }
      if (activeHeaderDropdown && !e.target.closest('.header-filter-dropdown')) {
        setActiveHeaderDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnSelector, activeHeaderDropdown]);

  // Close search help modal with Escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showSearchHelp) {
        setShowSearchHelp(false);
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [showSearchHelp]);

  // Format date/time for display in user's timezone
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    return formatInTimezone(dateTimeString, userTimezone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: undefined
    });
  };

  // Format date only for display in user's timezone
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatDateOnly(dateString, userTimezone);
  };

  // Format time only for display
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString;
  };

  // Helper function to convert local timezone datetime to UTC for server storage
  const convertToUTC = (localDateTime) => {
    if (!localDateTime) return null;
    return new Date(localDateTime).toISOString();
  };

  // Helper function to convert UTC datetime from server to local timezone for editing
  const convertFromUTC = (utcDateTime) => {
    if (!utcDateTime) return '';
    const date = new Date(utcDateTime);
    // Convert to local timezone and format for datetime-local input
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - (offset * 60 * 1000));
    return localDate.toISOString().slice(0, 16);
  };

  // Open export modal
  const openExportModal = () => {
    if (entries.length === 0) {
      alert('No data to export');
      return;
    }
    
    // Initialize export columns with currently visible columns
    const initialExportColumns = {};
    allColumns.forEach(column => {
      initialExportColumns[column.id] = visibleColumns[column.id] || false;
    });
    setExportColumns(initialExportColumns);
    setShowExportModal(true);
  };

  // Export to CSV function with async job system
  const exportToCSV = async (selectedColumns) => {
    setExportLoading(true);

    try {
      // Build query parameters - same as the data fetch
      let queryParams = new URLSearchParams();

      if (globalSearchTerm) {
        queryParams.append('search', globalSearchTerm);
      }

      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });

      // Add sorting to query params
      if (sorting.column) {
        queryParams.append('sort_by', sorting.column);
        queryParams.append('sort_direction', sorting.direction);
      }

      // Add selected columns as comma-separated string
      const selectedColumnIds = Object.entries(selectedColumns)
        .filter(([_, isSelected]) => isSelected)
        .map(([columnId, _]) => columnId);

      if (selectedColumnIds.length > 0) {
        queryParams.append('columns', selectedColumnIds.join(','));
      }

      // Close the export modal immediately
      setShowExportModal(false);
      setExportLoading(false);

      // Create export job
      const createResponse = await fetch(`/api/labor-export?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create export job');
      }

      const { jobId, filename } = await createResponse.json();

      // Initialize progress state
      setDownloadProgress({
        jobId,
        filename: filename || 'Preparing export...',
        progress: 0,
        bytesDownloaded: 0,
        totalBytes: 0,
        processedRows: 0,
        totalRows: 0,
        status: 'downloading'
      });

      // Poll for job status (WebSocket updates will also trigger updates)
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch(`/api/labor-export/${jobId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (!statusResponse.ok) {
            clearInterval(pollInterval);
            throw new Error('Failed to get job status');
          }

          const jobStatus = await statusResponse.json();

          // Update progress
          setDownloadProgress(prev => ({
            ...prev,
            status: jobStatus.status === 'completed' ? 'completed' : 'downloading',
            progress: jobStatus.progress || 0,
            processedRows: jobStatus.processedRows || 0,
            totalRows: jobStatus.totalRows || 0,
            filename: jobStatus.filename || prev.filename,
            error: jobStatus.error
          }));

          // If completed, download the file
          if (jobStatus.status === 'completed') {
            clearInterval(pollInterval);

            // Fetch the file with authentication
            const fileResponse = await fetch(`/api/labor-export/${jobId}/download`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            if (fileResponse.ok) {
              // Convert to blob and download
              const blob = await fileResponse.blob();
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', jobStatus.filename);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              // Update UI to show completion
              setDownloadProgress(prev => ({
                ...prev,
                status: 'completed',
                progress: 100
              }));

              // Auto-close after 3 seconds
              setTimeout(() => {
                setDownloadProgress(null);
              }, 3000);
            } else {
              throw new Error('Failed to download file');
            }
          }

          // If failed, stop polling
          if (jobStatus.status === 'failed') {
            clearInterval(pollInterval);
            setDownloadProgress(prev => ({
              ...prev,
              status: 'error',
              error: jobStatus.error || 'Export failed'
            }));

            // Auto-close after 8 seconds
            setTimeout(() => {
              setDownloadProgress(null);
            }, 8000);
          }
        } catch (error) {
          clearInterval(pollInterval);
          console.error('Error polling job status:', error);
        }
      }, 2000); // Poll every 2 seconds

      // Store interval ID for cleanup
      abortControllerRef.current = { pollInterval };

    } catch (error) {
      console.error('Error exporting to CSV:', error);

      let errorMessage = 'Failed to export data. Please try again.';

      if (error.name === 'AbortError') {
        errorMessage = 'Download cancelled by user';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Try reducing the date range or applying more filters.';
      } else if (error.message.includes('HTTP 401')) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.message.includes('HTTP 403')) {
        errorMessage = 'You do not have permission to export this data.';
      } else if (error.message.includes('HTTP 500')) {
        errorMessage = 'Server error. Please contact support if this persists.';
      } else if (error.message) {
        errorMessage = `Export failed: ${error.message}`;
      }

      setDownloadProgress(prev => prev ? {
        ...prev,
        status: 'error',
        error: errorMessage
      } : {
        filename: 'Export failed',
        progress: 0,
        bytesDownloaded: 0,
        totalBytes: 0,
        status: 'error',
        error: errorMessage
      });

      // Auto-close error toast after 8 seconds (longer to allow reading)
      setTimeout(() => {
        setDownloadProgress(null);
      }, 8000);
    } finally {
      setExportLoading(false);
    }
  };

  // Cancel download handler
  const handleCancelDownload = async () => {
    if (abortControllerRef.current && abortControllerRef.current.pollInterval) {
      clearInterval(abortControllerRef.current.pollInterval);
    }

    if (downloadProgress && downloadProgress.jobId) {
      try {
        await fetch(`/api/labor-export/${downloadProgress.jobId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } catch (error) {
        console.error('Error cancelling export:', error);
      }
    }

    setDownloadProgress(null);
    abortControllerRef.current = null;
  };

  // Close progress toast handler
  const handleCloseProgress = () => {
    setDownloadProgress(null);
  };

  // Export modal functions
  const handleExportColumnToggle = (columnId) => {
    setExportColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const handleSelectAllExportColumns = () => {
    const allSelected = {};
    allColumns.forEach(column => {
      allSelected[column.id] = true;
    });
    setExportColumns(allSelected);
  };

  const handleDeselectAllExportColumns = () => {
    const allDeselected = {};
    allColumns.forEach(column => {
      allDeselected[column.id] = false;
    });
    setExportColumns(allDeselected);
  };

  const handleExportConfirm = () => {
    const selectedCount = Object.values(exportColumns).filter(Boolean).length;
    if (selectedCount === 0) {
      alert('Please select at least one column to export');
      return;
    }
    
    exportToCSV(exportColumns);
    setShowExportModal(false);
  };

  // Toggle column visibility
  const toggleColumnVisibility = (columnId) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  // Handle row click to select entry
  const handleRowClick = (entry) => {
    setSelectedEntry(entry.id === selectedEntry?.id ? null : entry);
  };

  // Handle edit button click
  const handleEditClick = (e, entry) => {
    e.stopPropagation();
    setSelectedEntry(entry);
    setShowEditModal(true);
  };

  // Handle delete button click
  const handleDeleteClick = async (e, entryId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) {
      try {
        const response = await fetch(`/api/labor/${entryId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete entry');
        }
        
        // Remove deleted entry from state
        setEntries(entries.filter(entry => entry.id !== entryId));
        setSelectedEntry(null);
      } catch (error) {
        console.error('Error deleting entry:', error);
      }
    }
  };

  // Handle split button click
  const handleSplitClick = (e, entry) => {
    e.stopPropagation();
    
    // Check if the entry has a clock_out_time
    if (!entry.clock_out_time) {
      alert("Cannot split an entry for an employee who is currently clocked in. Please wait until they clock out.");
      return;
    }
    
    setSelectedEntry(entry);
    setShowSplitModal(true);
  };


// Handle split save
const handleSaveSplit = async (splitData) => {
  try {
    console.log('Splitting entry:', splitData);
    
    // Step 1: Update the original entry to shorten it
    const updateResponse = await fetch(`/api/labor/${splitData.originalEntryId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(splitData.originalEntryUpdate)
    });
    
    if (!updateResponse.ok) {
      const errorText = await updateResponse.text();
      console.error('Failed to update original entry:', errorText);
      throw new Error(`Failed to update original entry: ${updateResponse.status}`);
    }
    
    const updateResult = await updateResponse.json();
    console.log('Original entry updated:', updateResult);
    
    // Step 2: Create the new entry
    const createResponse = await fetch('/api/labor', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(splitData.newEntry)
    });
    
    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      console.error('Failed to create new entry:', errorText);
      // Try to revert the original entry update
      throw new Error(`Failed to create new entry: ${createResponse.status}`);
    }
    
    const createResult = await createResponse.json();
    console.log('New entry created:', createResult);
    
    // Refresh the entries list by triggering a re-fetch
    setFilters(prev => ({ ...prev }));
    
    setShowSplitModal(false);
    setSelectedEntry(null);
    alert("Entry split successfully! The original entry has been shortened and a new entry has been created.");
  } catch (error) {
    console.error('Error splitting entry:', error);
    alert(`Error splitting entry: ${error.message}`);
  }
};

// In your ClockInEntriesTab.js file, modify the handleSaveEdit function:
const handleSaveEdit = async (formData) => {
  try {
    console.log('Sending data:', formData);
    
    // Make a copy of the form data and ensure dates are properly formatted
    const preparedData = {...formData};
    
    // Make the API call
    const response = await fetch(`/api/labor/${selectedEntry.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(preparedData)
    });
    
    // Check for HTTP errors and try to get more details
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error response:', errorText);
      throw new Error(`Server error: ${response.status}`);
    }
    
    const updatedData = await response.json();
    console.log('Response data:', updatedData);
    
    // Update entries state
    setEntries(entries.map(entry => 
      entry.id === updatedData.entry.id ? updatedData.entry : entry
    ));
    
    setShowEditModal(false);
    alert("Entry updated successfully!");
  } catch (error) {
    console.error('Error updating entry:', error);
    alert(`Error updating entry: ${error.message}`);
  }
};


  // Header Filter Dropdown Component
  const HeaderFilterDropdown = ({ columnId, columnType, columnLabel }) => {
    const [localFilterState, setLocalFilterState] = useState(() => {
      // Initialize with current active filter or empty state
      const currentFilter = filters[columnId];
      if (columnType === 'integer') {
        return {
          exact: currentFilter || '',
          min: filters[`${columnId}_min`] || '',
          max: filters[`${columnId}_max`] || ''
        };
      } else if (columnType === 'text') {
        return {
          value: '',
          exactMatch: false
        };
      } else if (columnType === 'select') {
        const selectedValues = currentFilter ? currentFilter.split(',') : [];
        return {
          selectedValues
        };
      } else if (columnType === 'datetime' || columnType === 'date') {
        return {
          from: filters[`${columnId}_from`] ? new Date(filters[`${columnId}_from`]).toISOString().slice(0, 16) : '',
          to: filters[`${columnId}_to`] ? new Date(filters[`${columnId}_to`]).toISOString().slice(0, 16) : ''
        };
      }
      return {};
    });

    // State for search functionality in select dropdowns
    const [searchValue, setSearchValue] = useState('');

    const handleApply = () => {
      if (columnType === 'text') {
        // Text columns are now handled by global search, so just close dropdown
        setActiveHeaderDropdown(null);
      } else {
        // Handle other column types with filter parameters
        const filterData = {};
        
        if (columnType === 'integer') {
          if (localFilterState.exact) filterData[columnId] = localFilterState.exact;
          if (localFilterState.min) filterData[`${columnId}_min`] = localFilterState.min;
          if (localFilterState.max) filterData[`${columnId}_max`] = localFilterState.max;
        } else if (columnType === 'select') {
          if (localFilterState.selectedValues.length > 0) {
            filterData[columnId] = localFilterState.selectedValues.join(',');
          }
        } else if (columnType === 'datetime' || columnType === 'date') {
          if (localFilterState.from) filterData[`${columnId}_from`] = new Date(localFilterState.from).toISOString();
          if (localFilterState.to) filterData[`${columnId}_to`] = new Date(localFilterState.to).toISOString();
        }
        
        updateFilters(filterData);
        setActiveHeaderDropdown(null);
      }
    };

    const handleCancel = () => {
      setActiveHeaderDropdown(null);
    };

    const handleClear = () => {
      clearHeaderFilter(columnId);
    };

    const renderFilterControls = () => {
      if (columnType === 'integer') {
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Exact Value:</label>
              <input
                type="number"
                placeholder="Enter exact value"
                value={localFilterState.exact}
                onChange={(e) => setLocalFilterState(prev => ({ ...prev, exact: e.target.value }))}
                className="w-full bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Min:</label>
                <input
                  type="number"
                  placeholder="Min"
                  value={localFilterState.min}
                  onChange={(e) => setLocalFilterState(prev => ({ ...prev, min: e.target.value }))}
                  className="w-full bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Max:</label>
                <input
                  type="number"
                  placeholder="Max"
                  value={localFilterState.max}
                  onChange={(e) => setLocalFilterState(prev => ({ ...prev, max: e.target.value }))}
                  className="w-full bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        );
      } else if (columnType === 'text') {
        return (
          <div className="space-y-3">
            <div className="text-center p-4">
              <p className="text-slate-300 mb-2">Use the global search bar above to search text columns</p>
              <p className="text-xs text-slate-400">The search bar will look for matches across all columns including {columnLabel.toLowerCase()}</p>
            </div>
          </div>
        );
      } else if (columnType === 'select') {
        const options = filterOptions[columnId] || [];
        
        // Filter options based on search for full_name column
        const filteredOptions = columnId === 'full_name' && searchValue 
          ? options.filter(option => 
              option.toLowerCase().includes(searchValue.toLowerCase())
            )
          : options;
        
        return (
          <div>
            <label className="block text-sm text-slate-300 mb-2">Select values:</label>
            {columnId === 'full_name' && (
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="Search names..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="w-full bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
            <div className="max-h-32 overflow-y-auto space-y-1">
              {filteredOptions.map((option) => {
                const isSelected = localFilterState.selectedValues.includes(option);
                return (
                  <label key={option} className="flex items-center space-x-2 text-slate-300 text-sm">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...localFilterState.selectedValues, option]
                          : localFilterState.selectedValues.filter(v => v !== option);
                        setLocalFilterState(prev => ({ ...prev, selectedValues: newValues }));
                      }}
                      className="rounded text-blue-500 focus:ring-blue-500 bg-slate-600 border-slate-500"
                    />
                    <span>{option}</span>
                  </label>
                );
              })}
              {filteredOptions.length === 0 && columnId === 'full_name' && searchValue && (
                <div className="text-slate-400 text-sm py-2 text-center">
                  No names found matching "{searchValue}"
                </div>
              )}
            </div>
          </div>
        );
      } else if (columnType === 'datetime' || columnType === 'date') {
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">From:</label>
              <input
                type="datetime-local"
                value={localFilterState.from}
                onChange={(e) => setLocalFilterState(prev => ({ ...prev, from: e.target.value }))}
                className="w-full bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">To:</label>
              <input
                type="datetime-local"
                value={localFilterState.to}
                onChange={(e) => setLocalFilterState(prev => ({ ...prev, to: e.target.value }))}
                className="w-full bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        );
      }
      return null;
    };

    return (
      <div className="header-filter-dropdown absolute left-0 mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg z-50 w-72 p-4">
        <div className="mb-4">
          <h4 className="text-sm font-medium text-slate-200 mb-3">Filter {columnLabel}</h4>
          {renderFilterControls()}
        </div>
        
        <div className="flex space-x-2 pt-3 border-t border-slate-600">
          <button
            onClick={handleClear}
            className="flex-1 px-3 py-2 bg-slate-600 text-slate-200 rounded text-sm hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            Clear
          </button>
          <button
            onClick={handleCancel}
            className="flex-1 px-3 py-2 bg-slate-600 text-slate-200 rounded text-sm hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Apply
          </button>
        </div>
      </div>
    );
  };


  // Filter Summary Bar Component
  const FilterSummaryBar = () => {
    const activeCount = getActiveFilterCount();
    const summary = getActiveFilterSummary();
    
    if (activeCount === 0) return null;
    
    return (
      <div className="bg-slate-700 border border-slate-600 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium text-slate-300">
              {activeCount} active filter{activeCount !== 1 ? 's' : ''}
            </span>
          </div>
          
          <button
            onClick={clearAllFilters}
            className="text-sm text-slate-400 hover:text-slate-200 underline"
          >
            Clear all filters
          </button>
        </div>
        
        {summary.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {summary.map((filter, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-600 text-white"
              >
                {filter}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Get visible column count
  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;

  // Helper function to get display text for current date range
  const getDateRangeDisplayText = () => {
    switch (dateRangeType) {
      case 'today':
        return currentDateView.displayDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      case 'past7days':
        const sevenDaysAgo = new Date(currentDateView.start);
        return `${sevenDaysAgo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${currentDateView.displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'custom':
        const customStart = new Date(currentDateView.start);
        const customEnd = new Date(currentDateView.end);
        return `${customStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${customEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      default:
        return currentDateView.displayDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6 h-full flex flex-col overflow-hidden">
      {/* Header with search and column selector - fixed at top */}
{/* Combined header with all controls in one row */}
<div className="flex items-center justify-between mb-4">
  <div className="flex items-center space-x-4">
    <div className="flex items-center bg-slate-700 text-slate-200 border border-slate-600 rounded-md p-2">
      <Calendar className="h-5 w-5 mr-2 text-blue-400" />
      <span className="font-medium">
        {getDateRangeDisplayText()}
      </span>
    </div>
    
    <div className="flex space-x-2">
      <button
        onClick={() => handleDateRangeChange('today')}
        className={`rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          dateRangeType === 'today' 
            ? 'bg-blue-600 text-white hover:bg-blue-700' 
            : 'bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600'
        }`}
      >
        <div className="flex items-center">
          <CalendarDays className="h-5 w-5 mr-1" />
          <span>Today</span>
        </div>
      </button>
      
      <button
        onClick={() => handleDateRangeChange('past7days')}
        className={`rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          dateRangeType === 'past7days' 
            ? 'bg-blue-600 text-white hover:bg-blue-700' 
            : 'bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600'
        }`}
      >
        <div className="flex items-center">
          <CalendarRange className="h-5 w-5 mr-1" />
          <span>Past 7 Days</span>
        </div>
      </button>
      
      
      {/* Custom Date Range Selector */}
      <div className="flex items-center space-x-2 bg-slate-700 border border-slate-600 rounded-md p-2">
        <div className="flex items-center space-x-2">
          <label className="text-slate-300 text-sm">From:</label>
          <input
            type="date"
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
            className="bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <label className="text-slate-300 text-sm">To:</label>
          <input
            type="date"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
            className="bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <button
          onClick={handleCustomDateRangeSearch}
          className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        >
          <div className="flex items-center">
            <Search className="h-4 w-4 mr-1" />
            <span>Search</span>
          </div>
        </button>
      </div>
    </div>
  </div>
  
  <div className="flex items-center space-x-2">
    <button
      onClick={openExportModal}
      className="bg-green-600 text-white border border-green-500 rounded-md py-2 px-4 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2"
      title="Export data to CSV"
    >
      <Download className="h-5 w-5" />
      <span>Export CSV</span>
    </button>
    
    <div className="relative">
      <div className="flex items-center bg-slate-700 border border-slate-600 rounded-md px-3 py-2">
        <Search className="h-5 w-5 text-slate-400 mr-2" />
        <input
          type="text"
          placeholder="Search all columns..."
          value={globalSearchTerm}
          onChange={(e) => setGlobalSearchTerm(e.target.value)}
          className="bg-transparent text-slate-200 placeholder-slate-400 border-none outline-none focus:ring-0 w-64"
        />
        {globalSearchTerm && (
          <button
            onClick={() => setGlobalSearchTerm('')}
            className="ml-2 text-slate-400 hover:text-slate-200"
            title="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
    
    <div className="relative">
      <button
        className="column-selector bg-slate-700 text-slate-200 border border-slate-600 rounded-md py-2 px-4 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        onClick={() => setShowColumnSelector(!showColumnSelector)}
      >
        <div className="flex items-center">
          <AlignJustify className="h-5 w-5 mr-2" />
        </div>
      </button>
            
            {/* Column selector dropdown */}
            {showColumnSelector && (
              <div className="column-selector absolute right-0 mt-2 bg-slate-700 border border-slate-600 rounded-md shadow-lg z-50 p-4 w-64">
                <h3 className="text-slate-200 font-medium mb-2">Show/Hide Columns</h3>
                <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto">
                  {allColumns.map((column) => (
                    <label key={column.id} className="flex items-center space-x-2 text-slate-300">
                      <input
                        type="checkbox"
                        checked={visibleColumns[column.id]}
                        onChange={() => toggleColumnVisibility(column.id)}
                        className="rounded text-blue-500 focus:ring-blue-500 bg-slate-600 border-slate-500"
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Filter Summary Bar */}
      <FilterSummaryBar />
      
      {/* Table container */}
      <div 
        ref={tableContainerRef}
        className="relative flex-1 overflow-auto border border-slate-700 rounded-lg"
        style={{ height: 'calc(100vh - 300px)' }}
      >
        {/* Table with fixed layout for column alignment */}
        <table className="w-full text-left text-slate-300 table-fixed">
          {/* Header - sticky */}
          <thead className="sticky top-0 z-20 bg-slate-800 shadow-md">
            <tr className="border-b border-slate-700">
              {allColumns.map((column) => 
                visibleColumns[column.id] ? (
                  <th 
                    key={column.id}
                    className="py-3 px-4 font-medium relative"
                    style={{ width: columnWidths[column.id] }}
                  >
                    <div className="flex items-center justify-between whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <span className="flex items-center">
                          {column.label}
                          {sorting.column === column.id && (
                            sorting.direction === 'asc' ? (
                              <ArrowUp className="h-4 w-4 ml-1 text-blue-400" />
                            ) : (
                              <ArrowDown className="h-4 w-4 ml-1 text-blue-400" />
                            )
                          )}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        {/* Sort Button */}
                        <button
                          onClick={(e) => handleHeaderSortClick(column.id, e)}
                          className="p-1 hover:bg-slate-600 rounded transition-colors"
                          title={`Sort by ${column.label}`}
                        >
                          {sorting.column === column.id ? (
                            sorting.direction === 'asc' ? (
                              <ArrowUp className="h-3 w-3 text-blue-400" />
                            ) : (
                              <ArrowDown className="h-3 w-3 text-blue-400" />
                            )
                          ) : (
                            <ArrowUp className="h-3 w-3 text-slate-500 hover:text-slate-300" />
                          )}
                        </button>
                        
                        {/* Filter Button */}
                        <button
                          onClick={(e) => handleHeaderFilterClick(column.id, e)}
                          className={`p-1 hover:bg-slate-600 rounded transition-colors relative ${
                            hasActiveFilter(column.id) ? 'text-blue-400' : 'text-slate-500 hover:text-slate-300'
                          }`}
                          title={`Filter ${column.label}`}
                        >
                          <Filter className="h-3 w-3" />
                          {hasActiveFilter(column.id) && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Filter Dropdown */}
                    {activeHeaderDropdown === column.id && (
                      <HeaderFilterDropdown 
                        columnId={column.id}
                        columnType={column.type}
                        columnLabel={column.label}
                      />
                    )}
                  </th>
                ) : null
              )}
              <th className="py-3 px-4 font-medium" style={{ width: columnWidths.actions }}>
                <div className="flex items-center justify-center">
                  Actions
                </div>
              </th>
            </tr>
          </thead>
          
          {/* Table body - scrollable */}
          <tbody className="divide-y divide-slate-700" style={{ maxHeight: 'calc(100vh - 400px)' }}>
            {loading ? (
              <tr>
                <td colSpan={visibleColumnCount + 1} className="py-4 text-center text-slate-400">
                  Loading entries...
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnCount + 1} className="py-4 text-center text-slate-400">
                  No entries found for the selected date range
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr 
                  key={entry.id} 
                  className={`hover:bg-slate-700 cursor-pointer ${selectedEntry?.id === entry.id ? 'bg-slate-700' : ''}`}
                  onClick={() => handleRowClick(entry)}
                >
                  {visibleColumns.id && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.id }}>{entry.id}</td>
                  }
                  {visibleColumns.employee_id && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.employee_id }}>{entry.employee_id}</td>
                  }
                  {visibleColumns.full_name && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.full_name }}>{entry.full_name}</td>
                  }
                  {visibleColumns.activity && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.activity }}>{entry.activity}</td>
                  }
                  {visibleColumns.business_unit && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.business_unit }}>{entry.business_unit}</td>
                  }
                  {visibleColumns.clock_in_time && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.clock_in_time }}>{formatDateTime(entry.clock_in_time)}</td>
                  }
                  {visibleColumns.clock_out_time && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.clock_out_time }}>{formatDateTime(entry.clock_out_time)}</td>
                  }
                  {visibleColumns.total_time && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.total_time }}>{formatTime(entry.total_time)}</td>
                  }
                  {visibleColumns.group && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.group }}>{entry.group || '-'}</td>
                  }
                  {visibleColumns.project && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.project }}>{entry.project || '-'}</td>
                  }
                  {visibleColumns.shift && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.shift }}>{entry.shift || '-'}</td>
                  }
                  {visibleColumns.team && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.team }}>{entry.team || '-'}</td>
                  }
                  {visibleColumns.date && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.date }}>{formatDate(entry.date)}</td>
                  }
                  {visibleColumns.location && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.location }}>{entry.location || '-'}</td>
                  }
                  {visibleColumns.notes && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.notes }}>{entry.notes || '-'}</td>
                  }
                  {visibleColumns.reference1 && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.reference1 }}>{entry.reference1 || '-'}</td>
                  }
                  {visibleColumns.reference2 && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.reference2 }}>{entry.reference2 || '-'}</td>
                  }
                  {visibleColumns.reference3 && 
                    <td className="py-3 px-4 truncate" style={{ width: columnWidths.reference3 }}>{entry.reference3 || '-'}</td>
                  }
                  <td className="py-3 px-4" style={{ width: columnWidths.actions }}>
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={(e) => handleEditClick(e, entry)}
                        className="p-1 hover:bg-slate-600 rounded"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4 text-blue-400" />
                      </button>
                      <button 
                        onClick={(e) => handleDeleteClick(e, entry.id)}
                        className="p-1 hover:bg-slate-600 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </button>
                      <button 
                        onClick={(e) => handleSplitClick(e, entry)}
                        className={`p-1 rounded ${entry.clock_out_time ? 'hover:bg-slate-600' : 'opacity-50 cursor-not-allowed'}`}
                        title={entry.clock_out_time ? "Split" : "Cannot split - employee is still clocked in"}
                        disabled={!entry.clock_out_time}
                      >
                        <Scissors className={`h-4 w-4 ${entry.clock_out_time ? 'text-green-400' : 'text-slate-500'}`} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Edit Modal */}
      {showEditModal && selectedEntry && (
        <EditEntryModal 
          entry={selectedEntry} 
          onSave={handleSaveEdit}
          onCancel={() => setShowEditModal(false)}
          convertFromUTC={convertFromUTC}
          convertToUTC={convertToUTC}
        />
      )}
      
      {/* Split Modal */}
      {showSplitModal && selectedEntry && (
        <SplitEntryModal
          entry={selectedEntry}
          onSave={handleSaveSplit}
          onCancel={() => setShowSplitModal(false)}
          userTimezone={userTimezone}
          convertToUTC={convertToUTC}
        />
      )}
      
      {/* Search Help Modal */}
      {showSearchHelp && (
        <SearchHelpModal onClose={() => setShowSearchHelp(false)} />
      )}
      
      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          exportColumns={exportColumns}
          allColumns={allColumns}
          onToggleColumn={handleExportColumnToggle}
          onSelectAll={handleSelectAllExportColumns}
          onDeselectAll={handleDeselectAllExportColumns}
          onConfirm={handleExportConfirm}
          onCancel={() => setShowExportModal(false)}
          entriesCount={entries.length}
          isLoading={exportLoading}
        />
      )}

      {/* Download Progress Toast */}
      {downloadProgress && (
        <DownloadProgressToast
          filename={downloadProgress.filename}
          progress={downloadProgress.progress}
          bytesDownloaded={downloadProgress.bytesDownloaded}
          totalBytes={downloadProgress.totalBytes}
          processedRows={downloadProgress.processedRows}
          totalRows={downloadProgress.totalRows}
          status={downloadProgress.status}
          error={downloadProgress.error}
          onCancel={handleCancelDownload}
          onClose={handleCloseProgress}
        />
      )}
    </div>
  );
};

// Search Help Modal Component
const SearchHelpModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-slate-200">Search Help</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="space-y-4 text-slate-300">
          <div>
            <h4 className="font-medium text-slate-200 mb-2">Basic Search</h4>
            <div className="bg-slate-700 rounded p-3 space-y-2">
              <div className="flex justify-between">
                <code className="text-blue-400">John</code>
                <span className="text-sm text-slate-400">Search all columns for "John"</span>
              </div>
              <div className="flex justify-between">
                <code className="text-blue-400">Production</code>
                <span className="text-sm text-slate-400">Search all columns for "Production"</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-slate-200 mb-2">Column-Specific Search</h4>
            <div className="bg-slate-700 rounded p-3 space-y-2">
              <div className="flex justify-between">
                <code className="text-blue-400">name:John</code>
                <span className="text-sm text-slate-400">Search only in the name column</span>
              </div>
              <div className="flex justify-between">
                <code className="text-blue-400">activity:Production</code>
                <span className="text-sm text-slate-400">Search only in the activity column</span>
              </div>
              <div className="flex justify-between">
                <code className="text-blue-400">business_unit:QA</code>
                <span className="text-sm text-slate-400">Search only in the business unit column</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-slate-200 mb-2">Number Search</h4>
            <div className="bg-slate-700 rounded p-3 space-y-2">
              <div className="flex justify-between">
                <code className="text-blue-400">id:123</code>
                <span className="text-sm text-slate-400">Find exact ID 123</span>
              </div>
              <div className="flex justify-between">
                <code className="text-blue-400">id:&gt;100</code>
                <span className="text-sm text-slate-400">Find IDs greater than 100</span>
              </div>
              <div className="flex justify-between">
                <code className="text-blue-400">id:&lt;500</code>
                <span className="text-sm text-slate-400">Find IDs less than 500</span>
              </div>
              <div className="flex justify-between">
                <code className="text-blue-400">employee_id:&gt;=1000</code>
                <span className="text-sm text-slate-400">Find employee IDs 1000 or higher</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-slate-200 mb-2">Wildcard Search</h4>
            <div className="bg-slate-700 rounded p-3 space-y-2">
              <div className="flex justify-between">
                <code className="text-blue-400">activity:Prod*</code>
                <span className="text-sm text-slate-400">Activities starting with "Prod"</span>
              </div>
              <div className="flex justify-between">
                <code className="text-blue-400">name:*Smith</code>
                <span className="text-sm text-slate-400">Names ending with "Smith"</span>
              </div>
              <div className="flex justify-between">
                <code className="text-blue-400">location:*office*</code>
                <span className="text-sm text-slate-400">Locations containing "office"</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-slate-200 mb-2">Available Columns</h4>
            <div className="bg-slate-700 rounded p-3">
              <div className="grid grid-cols-3 gap-2 text-sm text-slate-400">
                <div>• id</div>
                <div>• employee_id</div>
                <div>• full_name (or name)</div>
                <div>• activity</div>
                <div>• business_unit</div>
                <div>• group</div>
                <div>• project</div>
                <div>• location</div>
                <div>• notes</div>
                <div>• reference1</div>
                <div>• reference2</div>
                <div>• reference3</div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-slate-200 mb-2">Tips</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-slate-400">
              <li>Use the column filter buttons for more advanced filtering options</li>
              <li>Combine search with filters for more precise results</li>
              <li>Search is case-insensitive by default</li>
              <li>Use quotes for exact phrases: name:"John Smith"</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Modal Component
const EditEntryModal = ({ entry, onSave, onCancel, convertFromUTC, convertToUTC }) => {
  const [formData, setFormData] = useState({
    activity: entry.activity || '',
    business_unit: entry.business_unit || '',
    group: entry.group || '',
    project: entry.project || '',
    shift: entry.shift || '',
    team: entry.team || '',
    location: entry.location || '',
    notes: entry.notes || '',
    reference1: entry.reference1 || '',
    reference2: entry.reference2 || '',
    reference3: entry.reference3 || '',
    // Format date and time for input elements (convert from UTC to local timezone)
    date: entry.date || '',
    clock_in_time: convertFromUTC(entry.clock_in_time),
    clock_out_time: convertFromUTC(entry.clock_out_time),
  });

  const [activities, setActivities] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [workGroups, setWorkGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all dropdown options when modal opens
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      setLoading(true);
      try {
        // Fetch activities
        const activitiesResponse = await fetch('/api/activities', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (activitiesResponse.ok) {
          const activitiesData = await activitiesResponse.json();
          setActivities(activitiesData);
        }
        
        // Fetch business units
        const businessUnitsResponse = await fetch('/api/business-units', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (businessUnitsResponse.ok) {
          const businessUnitsData = await businessUnitsResponse.json();
          setBusinessUnits(businessUnitsData);
        }
        
        // Fetch projects
        const projectsResponse = await fetch('/api/projects', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (projectsResponse.ok) {
          const projectsData = await projectsResponse.json();
          setProjects(projectsData);
        }
        
        // Fetch shifts
        const shiftsResponse = await fetch('/api/shifts', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (shiftsResponse.ok) {
          const shiftsData = await shiftsResponse.json();
          setShifts(shiftsData);
        }
        
        // Fetch teams
        const teamsResponse = await fetch('/api/teams', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (teamsResponse.ok) {
          const teamsData = await teamsResponse.json();
          setTeams(teamsData);
        }
        
        // Fetch work groups
        const workGroupsResponse = await fetch('/api/work-groups', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (workGroupsResponse.ok) {
          const workGroupsData = await workGroupsResponse.json();
          setWorkGroups(workGroupsData);
        }
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDropdownOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert datetime fields to UTC before sending to server
    const dataToSend = {
      ...formData,
      clock_in_time: formData.clock_in_time ? convertToUTC(formData.clock_in_time) : null,
      clock_out_time: formData.clock_out_time ? convertToUTC(formData.clock_out_time) : null,
    };
    
    onSave(dataToSend);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-slate-200 mb-4">Edit Entry #{entry.id}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Read-only fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">ID</label>
              <input 
                type="text" 
                value={entry.id} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Employee ID</label>
              <input 
                type="text" 
                value={entry.employee_id} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Full Name</label>
              <input 
                type="text" 
                value={entry.full_name} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Total Time</label>
              <input 
                type="text" 
                value={entry.total_time || 'N/A'} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            {/* Editable fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Activity</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="activity"
                  value={formData.activity}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an activity</option>
                  {activities.filter(activity => activity.is_active).map(activity => (
                    <option key={activity.id} value={activity.name}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Business Unit</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="business_unit"
                  value={formData.business_unit}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a business unit</option>
                  {businessUnits.filter(unit => unit.is_active).map(unit => (
                    <option key={unit.id} value={unit.identifier}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Group</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="group"
                  value={formData.group || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a group</option>
                  {workGroups.filter(group => group.is_active).map(group => (
                    <option key={group.id} value={group.name}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Project</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="project"
                  value={formData.project || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a project</option>
                  {projects.filter(project => project.is_active).map(project => (
                    <option key={project.id} value={project.name}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Shift</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="shift"
                  value={formData.shift || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a shift</option>
                  {shifts.map(shift => (
                    <option key={shift.id} value={shift.name}>
                      {shift.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Team</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="team"
                  value={formData.team || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Location</label>
              <input 
                type="text" 
                name="location"
                value={formData.location} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Date</label>
              <input 
                type="date" 
                name="date"
                value={formData.date} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Clock In Time</label>
              <input 
                type="datetime-local" 
                name="clock_in_time"
                value={formData.clock_in_time} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Clock Out Time</label>
              <input 
                type="datetime-local" 
                name="clock_out_time"
                value={formData.clock_out_time} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 1</label>
              <input 
                type="text" 
                name="reference1"
                value={formData.reference1} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 2</label>
              <input 
                type="text" 
                name="reference2"
                value={formData.reference2} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 3</label>
              <input 
                type="text" 
                name="reference3"
                value={formData.reference3} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-400 mb-1">Notes</label>
              <textarea 
                name="notes"
                value={formData.notes} 
                onChange={handleChange}
                rows="3"
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onCancel}
              className="bg-slate-700 text-slate-300 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Export Modal Component
const ExportModal = ({
  exportColumns,
  allColumns,
  onToggleColumn,
  onSelectAll,
  onDeselectAll,
  onConfirm,
  onCancel,
  entriesCount,
  isLoading
}) => {
  const selectedCount = Object.values(exportColumns).filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-slate-200">Export to CSV</h3>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-slate-300 mb-2">
            Select which columns to include in your CSV export ({entriesCount} entries)
          </p>
          <p className="text-sm text-slate-400">
            {selectedCount} of {allColumns.length} columns selected
          </p>
        </div>
        
        {/* Select All / Deselect All buttons */}
        <div className="flex space-x-3 mb-4">
          <button
            onClick={onSelectAll}
            className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Select All
          </button>
          <button
            onClick={onDeselectAll}
            className="bg-slate-600 text-slate-200 px-4 py-2 rounded text-sm hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            Deselect All
          </button>
        </div>
        
        {/* Column selection grid */}
        <div className="mb-6 max-h-96 overflow-y-auto border border-slate-600 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-2">
            {allColumns.map((column) => (
              <label key={column.id} className="flex items-center space-x-2 text-slate-300 p-2 hover:bg-slate-700 rounded">
                <input
                  type="checkbox"
                  checked={exportColumns[column.id] || false}
                  onChange={() => onToggleColumn(column.id)}
                  className="rounded text-blue-500 focus:ring-blue-500 bg-slate-600 border-slate-500"
                />
                <span className="text-sm">{column.label}</span>
                <span className="text-xs text-slate-400">({column.id})</span>
              </label>
            ))}
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="bg-slate-700 text-slate-300 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={selectedCount === 0 || isLoading}
            className={`px-4 py-2 rounded focus:outline-none focus:ring-2 ${
              selectedCount === 0 || isLoading
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
            }`}
          >
            <div className="flex items-center space-x-2">
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  <span>Export CSV ({selectedCount} columns)</span>
                </>
              )}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClockInEntriesTab;
